// import { PublicClientApplication, EventType, LogLevel } from "@azure/msal-browser";

// const msalConfig = {
//   auth: {
//     clientId:              process.env.REACT_APP_MSAL_CLIENT_ID,
//     authority:             `https://login.microsoftonline.com/${process.env.REACT_APP_MSAL_TENANT_ID}`,
//     redirectUri:           process.env.REACT_APP_MSAL_REDIRECT_URI,
//     postLogoutRedirectUri: process.env.REACT_APP_MSAL_POST_LOGOUT_REDIRECT_URI,
//     clientCapabilities:    ["CP1"],
//   },
//   cache: {
//     cacheLocation:          "localStorage",
//     storeAuthStateInCookie: true,
//   },
//   system: {
//     allowRedirectInIframe: true,   // ← ADD THIS
//     loggerOptions: {
//       loggerCallback: (level, message, containsPii) => {
//         if (containsPii) return;
//         switch (level) {
//           case LogLevel.Error:   console.error(message); break;
//           case LogLevel.Warning: console.warn(message);  break;
//           default:               break;
//         }
//       },
//     },
//   },
// };

// // Create instance only - initialize() is called once in index.js before render
// // Do NOT call initialize() here - calling it twice causes the redirect loop
// const msalInstance = new PublicClientApplication(msalConfig);



// // Sync active account on login and logout events
// msalInstance.addEventCallback((event) => {
//   if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
//     msalInstance.setActiveAccount(event.payload.account);
    
//   }
//   if (event.eventType === EventType.LOGOUT_SUCCESS) {
//     const accounts = msalInstance.getAllAccounts();
//     if (accounts.length > 0) {
//       msalInstance.setActiveAccount(accounts[0]);
      
      
//     }
//   }
// });

// export default msalInstance;


// // ── Scope definitions ─────────────────────────────────────────────────────────
// // loginRequest  → used for login + Microsoft Graph (profile, photo) — no change to existing login flow
// // apiRequest    → used for every PSI360 backend API call
// export const loginRequest = {
//   scopes: ["openid", "profile", "email", "User.Read"]
// };

import { PublicClientApplication, EventType, LogLevel, InteractionRequiredAuthError } from "@azure/msal-browser";

const msalConfig = {
  auth: {
    clientId:              process.env.REACT_APP_MSAL_CLIENT_ID,
    authority:             `https://login.microsoftonline.com/${process.env.REACT_APP_MSAL_TENANT_ID}`,
    redirectUri:           `${process.env.REACT_APP_MSAL_REDIRECT_URI}`,
    postLogoutRedirectUri: `${process.env.REACT_APP_MSAL_POST_LOGOUT_REDIRECT_URI}`,
    clientCapabilities:    ["CP1"],
  },
  cache: {
    cacheLocation:          "localStorage",
    storeAuthStateInCookie: true,
  },
  system: {
    allowRedirectInIframe: true,
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:   console.error(message); break;
          case LogLevel.Warning: console.warn(message);  break;
          default:               break;
        }
      },
    },
  },
};

const msalInstance = new PublicClientApplication(msalConfig);

// ── Auto-logout / token renewal helpers ──────────────────────────────────────

let _tokenExpiryTimer = null;

/**
 * Clears any running renewal/expiry timer.
 */
function clearExpiryTimer() {
  if (_tokenExpiryTimer !== null) {
    clearTimeout(_tokenExpiryTimer);
    _tokenExpiryTimer = null;
  }
}

/**
 * Schedules a silent token renewal 5 minutes BEFORE the token expires.
 *
 * Flow:
 *   Login → timer fires 55 min later → acquireTokenSilent
 *     ✅ success → ACQUIRE_TOKEN_SUCCESS event → scheduleAutoLogout resets timer
 *     ❌ fail    → refresh token gone → performLogout()
 *
 * @param {number} expiresOnSeconds  – token exp value in Unix seconds
 */
function scheduleAutoLogout(expiresOnSeconds) {
  clearExpiryTimer();

  const RENEW_BEFORE_MS  = 5 * 60 * 1000; // 5 minutes before expiry
  const msUntilRenewal   = expiresOnSeconds * 1000 - Date.now() - RENEW_BEFORE_MS;

  if (msUntilRenewal <= 0) {
    // Token already expired or expiring within the next 5 min → renew now
    console.warn("[MSAL] Token expiring very soon – attempting silent renewal immediately.");
    refreshAndReschedule();
    return;
  }

  console.log(
    `[MSAL] Token expires in ${Math.round((expiresOnSeconds * 1000 - Date.now()) / 1000)}s – ` +
    `silent renewal scheduled in ${Math.round(msUntilRenewal / 1000)}s.`
  );

  _tokenExpiryTimer = setTimeout(() => {
    console.log("[MSAL] Attempting silent token renewal before expiry...");
    refreshAndReschedule();
  }, msUntilRenewal);
}

/**
 * Silently acquires a fresh token.
 *   - Success → ACQUIRE_TOKEN_SUCCESS event fires → scheduleAutoLogout resets the timer.
 *   - InteractionRequiredAuthError → refresh token expired/revoked → performLogout().
 *   - Other errors → logged but not treated as fatal (network hiccup etc.).
 */
async function refreshAndReschedule(retryCount = 0) {
  const account = msalInstance.getActiveAccount();
  if (!account) return;

  try {
    await msalInstance.acquireTokenSilent({
      scopes:       ["openid", "profile", "email", "User.Read"],
      account,
      forceRefresh: false,
    });
    // Timer is reset inside the ACQUIRE_TOKEN_SUCCESS event callback below.
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Refresh token expired or admin revoked the session
      console.warn("[MSAL] Silent renewal failed (interaction required) – logging out.");
      performLogout();
    } else if (retryCount < 2) {
      // Likely a cross-tab cache race — back off and retry (max 2 times)
      const delay = 1000 * (retryCount + 1);
      console.warn(`[MSAL] Silent renewal error (attempt ${retryCount + 1}) – retrying in ${delay}ms.`, err);
      setTimeout(() => refreshAndReschedule(retryCount + 1), delay);
    } else {
      console.error("[MSAL] Silent renewal failed after retries (non-fatal):", err);
    }
  }
}

/**
 * SSO-aware logout:
 *   - Clears the renewal timer.
 *   - Calls logoutRedirect → ends the Azure AD session → SSO logout across all apps.
 */
async function performLogout() {
  clearExpiryTimer();
  try { await msalInstance.clearCache(); } catch (_) {}
  const account = msalInstance.getActiveAccount();
  msalInstance.logoutRedirect({
    account: account ?? undefined,
  }).catch((err) => console.error("[MSAL] Logout error:", err));
}

// ── Event callbacks ───────────────────────────────────────────────────────────

msalInstance.addEventCallback((event) => {

  // ── Login success → set active account + schedule first renewal ──────────
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
    msalInstance.setActiveAccount(event.payload.account);

    const expiresOn = event.payload.expiresOn;
    if (expiresOn) {
      scheduleAutoLogout(Math.floor(new Date(expiresOn).getTime() / 1000));
    } else {
      // expiresOn not present on this event – fetch token silently to read it
      refreshAndReschedule();
    }
  }

  // ── Silent/popup token renewal succeeded → reset the renewal timer ───────
  if (
    (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
     event.eventType === EventType.SSO_SILENT_SUCCESS) &&
    event.payload?.expiresOn
  ) {
    console.log("[MSAL] Token renewed successfully – rescheduling renewal timer.");
    scheduleAutoLogout(
      Math.floor(new Date(event.payload.expiresOn).getTime() / 1000)
    );
  }

  // ── Logout success → clear timer + handle remaining accounts ─────────────
  if (event.eventType === EventType.LOGOUT_SUCCESS) {
    clearExpiryTimer();

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
      refreshAndReschedule(); // restart renewal timer for the remaining account
    }
  }
});

// ── On page load: restore session + restart renewal timer ────────────────────
// Handles hard refresh / new tab — user already has a valid session in cache.
msalInstance.initialize().then(() => {
  const account =
    msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

  if (account) {
    msalInstance.setActiveAccount(account);
    // Random jitter (0–800ms) prevents cross-tab acquireTokenSilent collisions
    // when a CTA link is opened in a new tab while another tab is already active.
    const jitter = Math.floor(Math.random() * 800);
    setTimeout(() => refreshAndReschedule(), jitter);
  }
});

export default msalInstance;

// ── Scope definitions ─────────────────────────────────────────────────────────
export const loginRequest = {
  scopes: ["openid", "profile", "email", "User.Read"],
};

// Export performLogout so any component's "Sign out" button can use it
export { performLogout };